import React, { useState, useCallback, useMemo } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button, Grid, TextField, MenuItem } from '@mui/material';
import { SidePanelContent, SidePanelFooter } from '@securspace/securspace-ui-kit';
import { toast } from 'react-toastify';
import Busy from '../../components/Busy';
import { requestSetupIntent, confirmCardPaymentMethod } from '../../components/paymentMethods/request/payment-method-requests';
import AuthorizationCard from './AuthorizationCard';
import {
  CardFormContainer,
  FormSectionTitle,
  StripeBadge,
  PaymentElementWrapper,
  StripeSecureNote,
  Divider,
  BillingAddressSection,
  FieldError,
} from './styles/stripeCardFormStyle';
import { states } from '../../util/StateUtil';

interface StripeCardFormProps {
  platformPublishedKey: string;
  email: string;
  onSuccess: () => void;
  onCancel: () => void;
}

interface BillingAddress {
  name: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

interface BillingErrors {
  name?: string;
  line1?: string;
  city?: string;
  state?: string;
  postalCode?: string;
}

const INITIAL_ADDRESS: BillingAddress = {
  name: '',
  line1: '',
  line2: '',
  city: '',
  state: '',
  postalCode: '',
  country: 'US',
};

const CardFormInner = (props: { email: string; onSuccess: () => void; onCancel: () => void }) => {
  const { email, onSuccess, onCancel } = props;
  const stripe = useStripe();
  const elements = useElements();

  const [billing, setBilling] = useState<BillingAddress>(INITIAL_ADDRESS);
  const [errors, setErrors] = useState<BillingErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [nickName, setNickName] = useState('');

  const handleChange = useCallback((field: keyof BillingAddress) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setBilling((prev) => ({ ...prev, [field]: e.target.value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  }, []);

  const validate = useCallback((): boolean => {
    const next: BillingErrors = {};
    if (!billing.name.trim()) next.name = 'Name on card is required';
    if (!billing.line1.trim()) next.line1 = 'Street address is required';
    if (!billing.city.trim()) next.city = 'City is required';
    if (!billing.state) next.state = 'State is required';
    if (!billing.postalCode.trim()) next.postalCode = 'ZIP code is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  }, [billing]);

  const handleSubmit = useCallback(async () => {
    if (!stripe || !elements || submitting) return;
    if (!validate()) return;

    setSubmitting(true);
    Busy.set(true);

    try {
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          payment_method_data: {
            billing_details: {
              email,
              name: billing.name.trim(),
              phone: '',
              address: {
                line1: billing.line1.trim(),
                line2: billing.line2.trim() || undefined,
                city: billing.city.trim(),
                state: billing.state,
                postal_code: billing.postalCode.trim(),
                country: billing.country,
              },
            },
          },
        },
        redirect: 'if_required',
      });

      if (error) {
        toast.error(error.message || 'There was an error adding your card. Please try again.');
        return;
      }

      if (setupIntent && setupIntent.status === 'succeeded') {
        await confirmCardPaymentMethod(setupIntent.id, nickName.trim() || undefined);
        toast.success('Payment method added successfully');
        onSuccess();
      } else {
        toast.error('Card setup did not complete. Please try again.');
      }
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'An unexpected error occurred. Please try again.';
      toast.error(message);
    } finally {
      setSubmitting(false);
      Busy.set(false);
    }
  }, [stripe, elements, submitting, validate, billing, email, nickName, onSuccess]);

  const inputSx = {
    '& .MuiInputBase-input': { fontSize: '1em', padding: '0.65em 0.75em' },
    '& .MuiInputLabel-root': { fontSize: '0.86em' },
  };

  return (
    <>
      <SidePanelContent>
        <CardFormContainer>
          <FormSectionTitle>
            Card Details <StripeBadge>Stripe Secure</StripeBadge>
          </FormSectionTitle>

          <PaymentElementWrapper>
            <PaymentElement
              options={{
                fields: { billingDetails: 'never' },
                layout: 'tabs',
              }}
            />
            <StripeSecureNote>
              Secured by Stripe — card details never touch our servers
            </StripeSecureNote>
          </PaymentElementWrapper>

          <TextField
            fullWidth
            size="small"
            label="Name on Card"
            placeholder="Full name as shown on card"
            value={billing.name}
            onChange={handleChange('name')}
            error={!!errors.name}
            helperText={errors.name}
            sx={{ mb: '1em', ...inputSx }}
          />

          <TextField
            fullWidth
            size="small"
            label="Nickname (optional)"
            placeholder="e.g. Company Visa, Fleet Card"
            value={nickName}
            onChange={(e) => setNickName(e.target.value)}
            inputProps={{ maxLength: 50 }}
            sx={{ mb: '1em', ...inputSx }}
          />

          <Divider />

          <BillingAddressSection>
            <FormSectionTitle>Billing Address</FormSectionTitle>

            <TextField
              fullWidth
              size="small"
              label="Street Address"
              placeholder="123 Main St"
              value={billing.line1}
              onChange={handleChange('line1')}
              error={!!errors.line1}
              helperText={errors.line1}
              sx={{ mb: '1em', ...inputSx }}
            />

            <TextField
              fullWidth
              size="small"
              label="Apt, Suite, Unit (optional)"
              placeholder="Apt 4B"
              value={billing.line2}
              onChange={handleChange('line2')}
              sx={{ mb: '1em', ...inputSx }}
            />

            <Grid container spacing={2} sx={{ mb: '1em' }}>
              <Grid item xs={8}>
                <TextField
                  fullWidth
                  size="small"
                  label="City"
                  placeholder="City"
                  value={billing.city}
                  onChange={handleChange('city')}
                  error={!!errors.city}
                  helperText={errors.city}
                  sx={inputSx}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  select
                  size="small"
                  label="State"
                  value={billing.state}
                  onChange={handleChange('state')}
                  error={!!errors.state}
                  helperText={errors.state}
                  sx={inputSx}
                >
                  <MenuItem value="">State</MenuItem>
                  {[...states.values()].map((abbr) => (
                    <MenuItem key={abbr} value={abbr}>{abbr}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="ZIP Code"
                  placeholder="12345"
                  value={billing.postalCode}
                  onChange={handleChange('postalCode')}
                  error={!!errors.postalCode}
                  helperText={errors.postalCode}
                  inputProps={{ maxLength: 10 }}
                  sx={inputSx}
                />
              </Grid>
              <Grid item xs={8}>
                <TextField
                  fullWidth
                  select
                  size="small"
                  label="Country"
                  value={billing.country}
                  onChange={handleChange('country')}
                  sx={inputSx}
                >
                  <MenuItem value="US">United States</MenuItem>
                  <MenuItem value="CA">Canada</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </BillingAddressSection>
        </CardFormContainer>

        <Grid container>
          <AuthorizationCard payWithAch={false} />
        </Grid>
      </SidePanelContent>

      <SidePanelFooter>
        <Button variant="text" color="secondary" onClick={onCancel} disabled={submitting}>
          Back
        </Button>
        <Button variant="text" color="secondary" onClick={handleSubmit} disabled={!stripe || submitting}>
          Add Payment Method
        </Button>
      </SidePanelFooter>
    </>
  );
};

/**
 * Stripe.js keeps its page-level controller / Link iframes attached to the
 * document body even after `<Elements>` unmounts (they are singletons Stripe
 * reuses for the lifetime of the page). That leaves the Link autofill pill
 * floating on the viewport after the payment panel closes. Removing those
 * leftover Stripe frames on unmount makes the pill disappear with the panel;
 * Stripe re-injects them the next time the card form is opened, so in-form
 * Link pre-fill is unaffected.
 */
const removeStripeArtifacts = (): void => {
  if (typeof document === 'undefined') return;
  const selector = [
    'iframe[name^="__privateStripe"]',
    'iframe[src*="js.stripe.com"]',
    'iframe[src*="link.stripe.com"]',
  ].join(',');
  document.querySelectorAll(selector).forEach((node) => {
    // Element iframes live inside the (now unmounted) React tree; only the
    // body-level singletons remain. Guard against removing any still-mounted
    // element frames just in case cleanup order changes.
    if (!node.closest('.__PrivateStripeElement')) {
      node.remove();
    }
  });
};

const StripeCardForm = (props: StripeCardFormProps) => {
  const { platformPublishedKey, email, onSuccess, onCancel } = props;

  const stripePromise = useMemo(() => loadStripe(platformPublishedKey), [platformPublishedKey]);

  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    requestSetupIntent()
      .then((resp) => {
        if (!cancelled) setClientSecret(resp.clientSecret);
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError('Unable to initialize card setup. Please try again.');
          toast.error('Unable to initialize card setup. Please try again.');
        }
      });
    return () => { cancelled = true; };
  }, []);

  // Remove Stripe's leftover body-level iframes when the card form unmounts
  // (panel closed or "Back") so the floating Link pill goes away with it.
  React.useEffect(() => {
    return () => removeStripeArtifacts();
  }, []);

  if (loadError) {
    return (
      <>
        <SidePanelContent>
          <CardFormContainer>
            <FieldError>{loadError}</FieldError>
          </CardFormContainer>
        </SidePanelContent>
        <SidePanelFooter>
          <Button variant="text" color="secondary" onClick={onCancel}>
            Back
          </Button>
        </SidePanelFooter>
      </>
    );
  }

  if (!clientSecret) {
    return (
      <SidePanelContent>
        <CardFormContainer style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '10em' }}>
          Loading...
        </CardFormContainer>
      </SidePanelContent>
    );
  }

  return (
    <Elements stripe={stripePromise} options={{ clientSecret }}>
      <CardFormInner email={email} onSuccess={onSuccess} onCancel={onCancel} />
    </Elements>
  );
};

export default StripeCardForm;
